/*
** eSpace - the JS representations of the c++ qSpace object
** Copyright (C) 2021-2026 Tactile Interactive, all rights reserved
*/
import qeFuncs from './qeFuncs.js';
import qeConsts from './qeConsts.js';
import InteStats from '../controlPanel/InteStats.js';
//import {prepForDirectAccessors} from '../utils/directAccessors.js';
import voltDisplay from '../volts/voltDisplay.js';
import eObject from './eObject.js';
import eAvatar from './eAvatar.js';
import eGrinder from './eGrinder.js';
import eCavity from './eCavity.js';
import eFlick from './eFlick.js';
import {getAGroup} from '../utils/storeSettings.js';
import {interpretCppException} from '../utils/errors.js';
import {MAX_DIMENSIONS, N_THREADS} from './eEngine.js';
//import drawDesc2D from '../wave/drawDesc2D.js';

let traceSpace = false;
let traceFamiliarWave = false;
let traceGlobalSpace = false;


/* ************************************************ eSpace */
// this is how you create a qSpace - start from JS and call this.
// call like this:
// new eSpace([{N: 128, continuum: qeConsts.contENDLESS,coord: 'x', etc}], spaceLabelString)
//
// labels must be unique.  Modeled after qSpace in C++,
// does all dimensions in constructor, at least.
// Coords are the same if two dims are parallel, eg two particles with x coords.
// Not the same if one particle with x and y coords; eg you could have an endless canal.
export class eSpace extends eObject {
	// some bugs cause a big restart, creating a new space, and a big mess.
	static theSpace = null;

	constructor(dims, spaceLabel) {
		super();
		if (traceSpace)
			console.log(`eSpace constructor starting: dims=`, dims, spaceLabel);

		this.setPointer(qeFuncs.startNewSpace(spaceLabel));

		if (dims.length > MAX_DIMENSIONS)
			throw new Error(`Too many dimensions for space: ${dims.length} given but max is ${MAX_DIMENSIONS}`);
		// this._pointer_ = qeFuncs.startNewSpace(spaceLabel);
		// prepForDirectAccessors(this, this._pointer_);

		// make each dimension (someday there'll be more than 1)
		dims.forEach(d => {
			qeFuncs.addSpaceDimension(this._pointer_, d.N, d.continuum,
				d.dimLength, d.label);
			}
		);

		qeFuncs.completeNewSpace(this._pointer_, N_THREADS);

		// direct access into the voltage buffer
		this.voltageBuffer = new Float64Array(window.Module.HEAPF64.buffer,
				this._voltage, this.nPoints);
		this.vDisp = voltDisplay.newForSpace(this);
		let voltageParams = getAGroup('voltageParams');
		// not yet!  don't have display size this.vDisp.setAutoRange();  //(voltageParams);

		// the avatars create their vbufs and waves, and we make a copy for ourselves
		// no, done by scene this.mainAvatar = eAvatar.adaptAvatar(this._mainAvatar);
		// no, done by scene this.miniGraphAvatar = eAvatar.adaptAvatar(this._miniGraphAvatar);

		this.grinder = new eGrinder(this);
		this._grinder = this.grinder._pointer_;

		this.mainFlick = this.grinder.flick;  // i know its a flick not a wave
		this._mainFlick = this.mainFlick._pointer_;  // i know its a flick not a wave

		// SetWave most recent settings.
		let waveParams = getAGroup('waveParams');
		this.mainFlick.setFamiliarWave(waveParams);
		if (traceFamiliarWave)
			console.log(`🚀  done with setFamiliarWave() on main wave:`, this.mainFlick.wave);

		//this.sInteStats = new InteStats(this);
		if (traceSpace) console.log(`🚀  done creating eSpace:`, this);

		if (traceGlobalSpace)
			window.squishSpace = this;
	}

	// delete, except 'delete' is a reserved word.  Turn everything off.
	// null out all other JS objects and buffers it points to, so ref counting can recycle it all
	liquidate() {
		// delete stuff that this object's creator created, in reverse order
		try {
			if (traceSpace) {
				// trace msgs in C++ should agree with these
				let mgAv =  '0x'+ this.miniGraphAvatar._pointer_.toString(16);
				let mainAv = '0x'+  this.mainAvatar._pointer_.toString(16);
				let spP = '0x'+ this._pointer_.toString(16);
				console.log(`🚀  liquidating main avatar=${mainAv}  mini avatar=${mgAv} `
					+`  space=${spP} `,
					this);
			}
			this.miniGraphAvatar.liquidate();
			this.miniGraphAvatar = null;

			this.mainAvatar.liquidate();
			this.mainAvatar = null;

			// ?? aren't we freeing the voltage buffer?!?!
			this.voltageBuffer = this.dimensions = null;

			// finally, get rid of the C++ object
			qeFuncs.deleteFullSpace(this._pointer_);
			if (traceSpace) console.log(`🚀  done  liquidating eSpace:`, this);
		} catch (ex) {
			// eslint-disable-next-line no-ex-assign
			ex = interpretCppException(ex);
			console.error(ex.stack ?? ex.message ?? ex);
		}
	}

	/* *********************************    👽   👽    Direct Accessors */
	// see qSpace.cpp and directAccessors.h to regenerate this.

	get _voltage() { return this._ints_[1]; }

	get N() { return this._ints_[2]; }
	get continuum() { return this._ints_[3]; }
	get start() { return this._ints_[4]; }
	get end() { return this._ints_[5]; }
	get nStates() { return this._ints_[51]; }
	get nPoints() { return this._ints_[52]; }
	get dimLength() { return this._doubles_[4]; }
	get refDt() { return this._doubles_[24]; }
	set refDt(a) { this._doubles_[24] = a; }
	get nDimensions() { return this._ints_[50]; }
	get spectrumLength() { return this._ints_[53]; }
	get _mainFlick() { return this._ints_[54]; }
	set _mainFlick(a) { this._ints_[54] = a; }
	get _grinder() { return this._ints_[55]; }
	set _grinder(a) { this._ints_[55] = a; }
	get _label() { return this._pointer_ + 224; }


	/* ****************************    👽   👽    end of direct accessors */

	/* ******************************************** rendering helpers */

		// the actual datapoints are on the edges between segments.
		// So, 8 states means nPoints=10 means 9 or 10 segments.  depending.
		// For contENDLESS, this means 8 segments
		// drawn.  Resonant wavelength is 8.
		// For contWELL, this means the data edges on each end are zero, with 8
		//     actual datapoints for the state.  10 segments drawn, although the
		//     outer 2 go off to infinity.  Resonant wavelength is 9.


	// calculate the relative barWidth given N and the continuum.  a fraction of 1.
	// So everybody's on the same page with it.
	// Also start and end for drawing onscreen cuz continuum
	// use it like this: d3.scaleLinear([start, end], leftCoord, rightCoord);
	// then loop 1...end or 0...(start+end) if you need the endpoints

	// THIS IS ABSORBED INTO voltDisplay
// 	gesfghsfght drawDesczfghsfgh2D() {
// 		if (!this._drawDesc2D)
// 			this._drawDesc2D = new drawDesc2D(this);
// 		return this._drawDesc2D;
// 		// maybe this should calculate each component on the fly with multiple getters?
// 		// TODO
//
// 		// const space = this;
// 		// if (space.continuum == qeConsts.contENDLESS) {
// 		// 	// eg for N=8, 8 segments and segment 0===8 and 1===9
// 		// 	return {barWidth: 1 / this.nStates, start: this.start, end: this.end};
// 		// }
// 		// else if (space.continuum == qeConsts.contWELL) {
// 		// 	// eg for N=8, 8 segments plus two on ends that go to ∞,
// 		// 	// so segment 0===9=∞
// 		// 	return {barWidth: 1 / this.nPoints, start: 0, end: this.end + this.start};
// 		// }
// 		// else {
// 		// 	throw `bad continuum ${this.continuum}`;
// 		// }
// 	}


	// return me the start, end, etc of this 1d space
	// call it like this: const {start, end, N, continuum} = this.startEnd;
	get startEnd() {
		//const dim = this.dimensions[0];
		return {start: this.start, end: this.end, N: this.N, nPoints: this.nPoints,
			continuum: this.continuum, dimLength: this.dimLength};
		//const dim = this.dimensions[0];
		//return {start: dim.start, end: dim.end, N: dim.N, nPoints: this.nPoints,
		//	continuum: dim.continuum, dimLength: dim.dimLength};
	}

	// this will return the DOUBLE of start and end, for indexing into complex tables.
	// so you can just loop thru += 2
	// skipping over reals/imags.  but NOT N, that's honest
	// call like this:       const {start2, end2, N} = this.space.startEnd2;
	// note start2, end2 NEED TO BE SPELLED exactly that way!
	get startEnd2() {
		return {start2: this.start*2, end2: this.end*2, N: this.N, nPoints2: this.nPoints*2,
			continuum: this.continuum, dimLength: this.dimLength};
	}
}

export default eSpace;
